import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GetLocationsDocument } from '@hopdrive/feature-move-planner';
import { Box, CircularProgress, Typography, useTheme, Alert, Collapse } from '@mui/material';
import dayjs from 'dayjs';
import Timeline from '@components/Timeline/Timeline/Timeline';
import ScheduleHeader from './ScheduleHeader';
import ScheduleIndexRow from './ScheduleIndexRow';
import ScheduleAreaRow from './ScheduleAreaRow';
import SidePanel from '@components/SidePanel/SidePanel';
import MoveDetailsPanelContent from './MoveDetailsPanelContent';
import MoveCreationPanelContent from './MoveCreationPanelContent';
import { useScheduleData } from '../hooks/useScheduleData';
import { useTimelineLaneBuilder } from '../hooks/useTimelineLaneBuilder';
import { useBusinessHoursForTimeline } from '../hooks/useBusinessHoursForTimeline';
import { Scenarios, Locations, Moves } from '@gql/base-types';
import { useRooftop } from '@store';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';
import { getGapBetweenTimes } from '@components/Timeline/Timeline/utils';
import { getRooftopTimezone } from '@utils/timezoneUtils';
import { useValetSelfManaged, capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import { OptimisticMoveProvider, useOptimisticMoves } from '../context/OptimisticMoveContext';
import { useMultipleDriverSchedules } from '@features/drivers/hooks/useDriverSchedule';
import { processDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';

interface ScheduleTimelineProps {
  onScenarioClick?: (scenario: Scenarios) => void;
}

interface PlanningContext {
  pickup: Locations;
  delivery: Locations;
  dateTime: Date;
  driverId?: number;
}

const ScheduleTimeline = ({ onScenarioClick }: ScheduleTimelineProps) => {
  const theme = useTheme();
  const rooftop = useRooftop();
  const hasPlanningFeature = useFeaturePermission(FeatureName.PLANNING, rooftop?.id);
  const hasDispatchingFeature = useFeaturePermission(FeatureName.DISPATCHING, rooftop?.id);

  // Extract appointment management config
  const appointmentConfig = React.useMemo(() => {
    if (!rooftop?.config) return {
      moveBufferMinutes: 0,
      fullPlanningEnabled: false,
      offsetIncrementMinutes: 0,
      offsetMaxMinutes: 0,
    };

    try {
      const config = typeof rooftop.config === 'string' ? JSON.parse(rooftop.config) : rooftop.config;
      return {
        moveBufferMinutes: config?.appointment_management?.move_buffer_minutes || 0,
        fullPlanningEnabled: config?.appointment_management?.full_planning_enabled === true,
        offsetIncrementMinutes: config?.appointment_management?.offset_increment_minutes || 0,
        offsetMaxMinutes: config?.appointment_management?.offset_max_minutes || 0,
      };
    } catch (e) {
      console.error('Error parsing rooftop config for appointment_management:', e);
      return {
        moveBufferMinutes: 0,
        fullPlanningEnabled: false,
        offsetIncrementMinutes: 0,
        offsetMaxMinutes: 0,
      };
    }
  }, [rooftop?.config]);

  const moveBufferMinutes = appointmentConfig.moveBufferMinutes;
  const fullPlanningEnabled = appointmentConfig.fullPlanningEnabled;
  const offsetIncrementMinutes = appointmentConfig.offsetIncrementMinutes;
  const offsetMaxMinutes = appointmentConfig.offsetMaxMinutes;

  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Locations | null>(null);
  const [timelineZoom] = useState(4); // 4 pixels per minute
  const [selectedMove, setSelectedMove] = useState<Moves | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create' | null>(null);
  const [timelineScrollFn, setTimelineScrollFn] = useState<((pos: number) => void) | null>(null);

  // Decouple panel visibility from mounting for optimistic UI
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  // Planning state
  const [planningAddress, setPlanningAddress] = useState<Locations | null>(null);
  const [planningContext, setPlanningContext] = useState<PlanningContext | null>(null);
  const [locations, setLocations] = useState<Locations[]>([]);
  const [selectedAddressForInput, setSelectedAddressForInput] = useState<Locations | null>(null);
  // Scenario type for planning overlay - determines slot duration calculation
  // 'concierge-pu': pickup from rooftop, deliver to customer (travel + service)
  // 'concierge-ret': return car to customer (travel only, no service after)
  // TODO: Add UI selector to toggle between scenario types
  const [planningScenarioKey, setPlanningScenarioKey] = useState<string>('concierge-pu');
  void setPlanningScenarioKey; // Will be used when UI selector is added

  // Loading state for overlays
  const [isLoadingOverlays, setIsLoadingOverlays] = useState(false);

  // Fetch and process data
  const { timelineData, loading, error, algorithm, driverCount, refetch } = useScheduleData({ selectedDate });

  // Self-managed (lite): the working customer runs its own drivers, so the timeline dispatches
  // to real drivers; hopdrive-managed plans with anonymous drivers.
  const isLiteTier = useValetSelfManaged();

  // Lite: creating from timeline = assigning to a real driver (dispatching).
  // Standard: creating from timeline = scheduling with anonymous drivers (planning).
  // Creating a move from the timeline also requires the Valet product's createNew capability
  // (a read_only product may view the schedule but not create), on top of the feature permission.
  const canCreateNew = capabilityGate(useProductEntry('moves'), 'createNew').kind === 'render';
  const canCreateFromTimeline = canCreateNew && (isLiteTier ? hasDispatchingFeature : hasPlanningFeature);

  const driverIdsForSchedule = React.useMemo(() => {
    if (!isLiteTier) return [];
    return timelineData
      .filter((row) => row.driver !== null)
      .map((row) => row.driver!.id)
      .filter((id): id is number => id !== undefined);
  }, [timelineData, isLiteTier]);

  // Fetch driver schedules for all drivers (self-managed only)
  const { schedulesByDriverId, loading: loadingSchedules } = useMultipleDriverSchedules(driverIdsForSchedule);

  // Get optimistic moves context for cleanup
  const { optimisticMoves, removeOptimisticMove, updateOptimisticMove } = useOptimisticMoves();

  // Lane builder for planning overlays (display only - not passed to MovePlannerProvider)
  const { buildLane, lane: timelineLane, clearLane, error: laneBuilderError, enriching: laneCalculating } = useTimelineLaneBuilder();

  // Fetch business hours for timeline boundaries
  const businessHours = useBusinessHoursForTimeline(
    rooftop?.location?.id?.toString(),
    selectedDate,
    getRooftopTimezone(rooftop)
  );

  // Check if it's today and past closing time (planning should be disabled)
  const isPastClosingTimeToday = React.useMemo(() => {
    const isToday = selectedDate === dayjs().format('YYYY-MM-DD');
    if (!isToday) return false;

    const timezone = getRooftopTimezone(rooftop);
    const now = dayjs().tz(timezone);
    const [hours, minutes, seconds] = businessHours.closeTime.split(':').map(Number);
    const closingTime = dayjs().tz(timezone).hour(hours).minute(minutes).second(seconds || 0);

    return now.isAfter(closingTime);
  }, [selectedDate, businessHours.closeTime, rooftop?.location?.timezone]);

  // Fetch locations for the rooftop
  const [getLocations] = useLazyQuery(GetLocationsDocument);

  // Fetch locations when rooftop changes
  useEffect(() => {
    const fetchLocations = async () => {
      if (rooftop?.id) {
        const result = await getLocations({
          variables: { rooftopId: rooftop.id },
        });
        if (result.data?.locations) {
          setLocations(result.data.locations as Locations[]);
        }
      }
    };

    fetchLocations();
  }, [rooftop, getLocations]);

  // Handle scenario click
  const handleScenarioClick = (scenario: Scenarios) => {
    // Extract the first drive move from the scenario
    const driveMove = scenario.moves
      ?.map((link) => link.move)
      .find((move) => move?.move_type === 'drive');

    if (driveMove) {
      setSelectedMove(driveMove as Moves);
      setPanelMode('view');
      setIsPanelVisible(true);  // Show panel visually
    }

    // Call the optional callback if provided
    if (onScenarioClick) {
      onScenarioClick(scenario);
    }
  };

  // Handle panel close - decouple visibility from mounting
  const handlePanelClose = useCallback(() => {
    // Always hide the panel visually
    setIsPanelVisible(false);

    // For 'create' mode: never unmount here - let handleDispatchComplete do it
    // This prevents race conditions where the panel closes before dispatch starts
    if (panelMode === 'create') {
      return;
    }

    // For 'view' mode: safe to unmount immediately
    setPanelMode(null);
    setSelectedMove(null);
  }, [panelMode]);

  // Handle dispatch complete (called when dispatch finishes or times out)
  // This is the ONLY place where panelMode='create' gets cleared
  const handleDispatchComplete = useCallback((_optimisticId: string, success: boolean) => {
    setPanelMode(null);        // Now safe to unmount
    setSelectedMove(null);

    // Refetch to get fresh data
    if (success) {
      refetch();
    }
  }, [refetch]);

  // Handle submit start - clear planning overlays immediately
  const handleSubmitStart = useCallback(() => {
    setPlanningAddress(null);
    setSelectedLocation(null);
    setSelectedAddressForInput(null);
  }, []);

  // Handle successful move creation
  const handleMoveCreated = React.useCallback(() => {
    // Refetch immediately to show new move
    refetch();
  }, [refetch]);

  // Handle driver row click
  const handleDriverClick = (driverId: number | null) => {
    setSelectedDriverId(driverId);
  };

  // Handle address selection for planning
  const handleAddressSelect = (location: Locations | null, isNewLocation?: boolean) => {
    // Update the input's current value (used by MovePlannerLocationSelect to prevent re-triggering build)
    setSelectedAddressForInput(location);

    // Handle explicit clear (X button clicked)
    if (location === null) {
      setSelectedLocation(null);
      setPlanningAddress(null);
      return;
    }

    // Only accept locations that have been created in the database (have an id)
    if (!location?.id) {
      return;
    }

    // Valid location selected
    setSelectedLocation(location);
    setPlanningAddress(location);

    // If a new location was created, add it to the locations array
    if (isNewLocation && location) {
      setLocations((prev) => [...prev, location]);
    }
  };

  // Build lane for overlay display when address selected
  useEffect(() => {
    const buildPlanningLane = async () => {
      if (planningAddress && rooftop?.location) {
        setIsLoadingOverlays(true);
        await buildLane(rooftop.location, planningAddress, rooftop.id);
        setIsLoadingOverlays(false);
      } else {
        clearLane();
        setIsLoadingOverlays(false);
      }
    };
    buildPlanningLane();
  }, [planningAddress, rooftop, buildLane, clearLane]);

  // Debounce ref for rapid slot clicks to prevent provider mount/unmount race conditions
  const lastSlotClickRef = useRef<number>(0);
  const SLOT_CLICK_DEBOUNCE_MS = 300;

  // Handle time slot click from planning overlay
  const handleTimeSlotClick = (startTime: Date, driverId?: number) => {
    // Gate behind dispatching feature permission
    if (!canCreateFromTimeline) return;

    // Debounce rapid clicks to prevent provider mount/unmount race conditions (React Error #185)
    const now = Date.now();
    if (now - lastSlotClickRef.current < SLOT_CLICK_DEBOUNCE_MS) {
      return; // Ignore click if too soon after last one
    }
    lastSlotClickRef.current = now;

    // If panel is still in 'create' mode (provider mounted but hidden), reset it first
    // This prevents updating planningContext while the old provider is still mounted
    if (panelMode === 'create' && !isPanelVisible) {
      setPanelMode(null);
      // Use double requestAnimationFrame to ensure React completes cleanup before remounting
      // This is more reliable than a fixed timeout as it waits for actual render cycles
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (planningAddress && rooftop?.location) {
            setPlanningContext({
              pickup: rooftop.location,
              delivery: planningAddress,
              dateTime: startTime,
              driverId,
            });
            setPanelMode('create');
            setIsPanelVisible(true);
          }
        });
      });
      return;
    }

    if (planningAddress && rooftop?.location) {
      setPlanningContext({
        pickup: rooftop.location,
        delivery: planningAddress,
        dateTime: startTime,
        driverId,
      });
      setPanelMode('create');
      setIsPanelVisible(true);  // Show panel visually
    }
  };

  // Filter data by selected location if provided
  const filteredData = React.useMemo(() => {
    // When planning (address selected for creating new moves), show all driver rows but exclude unassigned
    if (planningAddress) {
      return timelineData.filter((row) => row.driver !== null);
    }

    // Filter out unassigned rows when not searching for a location
    if (!selectedLocation) {
      return timelineData.filter((row) => row.driver !== null);
    }

    const searchAddress = selectedLocation.address?.toLowerCase() || '';
    const searchLat = selectedLocation.latitude;
    const searchLng = selectedLocation.longitude;

    return timelineData
      .map((row) => ({
        ...row,
        scenarios: row.scenarios.filter((scenario) => {
          // Check if any move in the scenario matches the location
          const moves = scenario.moves?.map((link) => link.move) || [];

          return moves.some((move) => {
            const pickupAddress = move.lane?.pickup?.google_address || '';
            const deliveryAddress = move.lane?.delivery?.google_address || '';
            const pickupLat = move.lane?.pickup?.latitude;
            const pickupLng = move.lane?.pickup?.longitude;
            const deliveryLat = move.lane?.delivery?.latitude;
            const deliveryLng = move.lane?.delivery?.longitude;

            // Check address match
            const addressMatch =
              pickupAddress.toLowerCase().includes(searchAddress) ||
              deliveryAddress.toLowerCase().includes(searchAddress);

            // Check coordinate proximity (within ~0.01 degrees, roughly 1km)
            const pickupCoordMatch =
              searchLat &&
              searchLng &&
              pickupLat &&
              pickupLng &&
              Math.abs(pickupLat - searchLat) < 0.01 &&
              Math.abs(pickupLng - searchLng) < 0.01;

            const deliveryCoordMatch =
              searchLat &&
              searchLng &&
              deliveryLat &&
              deliveryLng &&
              Math.abs(deliveryLat - searchLat) < 0.01 &&
              Math.abs(deliveryLng - searchLng) < 0.01;

            return addressMatch || pickupCoordMatch || deliveryCoordMatch;
          });
        }),
      }))
      .filter((row) => row.scenarios.length > 0); // Only show rows with matching scenarios when filtering by location
  }, [timelineData, selectedLocation, planningAddress]);

  // Compute if any slots are available when planning
  const noSlotsAvailable = React.useMemo(() => {
    // Only compute when actively planning with a valid lane
    if (!planningAddress || !timelineLane?.duration_sec || !algorithm || isLoadingOverlays) {
      return false;
    }

    // Wait for driver schedules to load for a self-managed customer
    if (isLiteTier && loadingSchedules) {
      return false;
    }

    // Don't show "no slots" if business is closed - those have their own banners
    if (businessHours.isClosed || isPastClosingTimeToday) {
      return false;
    }

    // Get driver scenarios (same merged list the timeline overlay and slot generator use):
    // allScenarios includes assigned + distributed + offered, so the no-slots check sees a
    // driver's true busy day. Using scenarios/offeredScenarios alone omits distributed
    // scenarios (mainly hopdrive-managed) and under-counts the day, hiding this warning.
    const driverRows = timelineData.filter((row) => row.driver !== null);
    const allDriverScenarios = driverRows.map((row) => row.allScenarios);
    const actualDriverCount = driverRows.length || 1;
    const timezone = getRooftopTimezone(rooftop);

    // Check each driver row for available slots
    let totalSlots = 0;
    for (let driverIndex = 0; driverIndex < actualDriverCount; driverIndex++) {
      // Self-managed: get the driver's schedule and process it
      let driverSchedule;
      if (isLiteTier && driverRows[driverIndex]?.driver?.id) {
        const driverId = driverRows[driverIndex].driver!.id;
        const rawSchedule = schedulesByDriverId.get(driverId);
        if (rawSchedule) {
          driverSchedule = processDriverSchedule(rawSchedule as any[], String(driverId));
        }
      }

      const slots = algorithm.generateTimeSlots({
        driverIndex,
        driverCount: actualDriverCount,
        allDriverScenarios,
        lane: timelineLane,
        businessHours: {
          openTime: businessHours.openTime,
          closeTime: businessHours.closeTime,
        },
        selectedDate,
        timezone,
        bufferMinutes: moveBufferMinutes,
        tier: isLiteTier ? 'lite' : 'standard',
        driverSchedule,
        scenarioKey: planningScenarioKey,
      });
      totalSlots += slots.length;
      // Early exit if we find any slots
      if (totalSlots > 0) {
        return false;
      }
    }

    return totalSlots === 0;
  }, [planningAddress, timelineLane, algorithm, isLoadingOverlays, isLiteTier, loadingSchedules, businessHours, isPastClosingTimeToday, timelineData, rooftop?.location?.timezone, schedulesByDriverId, selectedDate, moveBufferMinutes, planningScenarioKey]);

  // Set timeline start and end times based on business hours (in customer timezone)
  const customerTimezone = getRooftopTimezone(rooftop);
  const timelineStart = React.useMemo(() => {
    const [hours, minutes, seconds] = businessHours.openTime.split(':').map(Number);
    return dayjs.tz(selectedDate, customerTimezone).hour(hours).minute(minutes).second(seconds || 0).format();
  }, [selectedDate, businessHours.openTime, customerTimezone]);

  const timelineEnd = React.useMemo(() => {
    const [hours, minutes, seconds] = businessHours.closeTime.split(':').map(Number);
    return dayjs.tz(selectedDate, customerTimezone).hour(hours).minute(minutes).second(seconds || 0).format();
  }, [selectedDate, businessHours.closeTime, customerTimezone]);

  // Compute global set of ASSIGNED move IDs across all rows (for optimistic block visibility)
  // Only includes scenarios (assigned/accepted moves), NOT offeredScenarios (pending offers)
  // This ensures optimistic block stays visible until the move is actually assigned
  const globalRealMoveIds = React.useMemo(() => {
    const ids = new Set<number>();
    timelineData.forEach((row) => {
      row.scenarios.forEach((scenario) => {
        scenario.moves?.forEach((moveLink) => {
          if (moveLink.move?.id) {
            ids.add(moveLink.move.id);
          }
        });
      });
      // NOTE: Intentionally NOT including offeredScenarios here
      // Optimistic block should stay visible while offer is pending
    });
    return ids;
  }, [timelineData]);

  // Auto-scroll timeline when the date changes: to the current time when viewing
  // today, otherwise to the day's start time.
  useEffect(() => {
    if (timelineScrollFn && timelineStart) {
      const startOfDay = dayjs.tz(selectedDate, customerTimezone).startOf('day').format();
      const isToday = selectedDate === dayjs().format('YYYY-MM-DD');
      const scrollTarget = isToday ? dayjs().format() : timelineStart;
      const scrollPosition = getGapBetweenTimes(startOfDay, scrollTarget, timelineZoom);
      // Scroll to the target time with a small offset to show a bit before it
      timelineScrollFn(Math.max(0, scrollPosition - 100));
    }
  }, [selectedDate, timelineScrollFn, timelineStart, timelineZoom, customerTimezone]);

  // Clean up completed optimistic moves when real data arrives (move is ASSIGNED, not just offered)
  // Use a ref to track pending removals and prevent duplicate timeouts
  const pendingRemovalsRef = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    // Get ASSIGNED move IDs from timeline data (not offered scenarios)
    const assignedMoveIds = new Set<number>();
    timelineData.forEach((row) => {
      row.scenarios.forEach((scenario) => {
        scenario.moves?.forEach((moveLink) => {
          if (moveLink.move?.id) {
            assignedMoveIds.add(moveLink.move.id);
          }
        });
      });
      // NOTE: Intentionally NOT including offeredScenarios
      // Only cleanup when move is assigned, not while offer is pending
    });

    // Remove optimistic moves that are now assigned
    optimisticMoves.forEach((optMove) => {
      // Skip if already scheduled for removal
      if (pendingRemovalsRef.current.has(optMove.id)) {
        return;
      }

      // The real move has been assigned somewhere on the timeline — remove the optimistic
      // tile by its global move id. Removal must NOT be scoped to the row the tile was
      // assumed to live in: rowless moves (legacy, embed/API-created — no persisted
      // `config.driver_row_number`) are distributed across Standard rows at runtime, and even
      // a row-carrying move claims the first conflict-free row rather than the row the user
      // clicked — scoped removal left tiles stuck whenever the move appeared on another row.
      if (optMove.actualMoveId && assignedMoveIds.has(optMove.actualMoveId)) {
        // Mark as pending removal to prevent duplicate timeouts
        pendingRemovalsRef.current.add(optMove.id);
        // Delay to allow fade-out (500ms) + buffer for the real scenario block to render.
        // The component fades via the isRealMovePresent prop, then we remove it from context.
        setTimeout(() => {
          removeOptimisticMove(optMove.id);
          pendingRemovalsRef.current.delete(optMove.id);
        }, 1500); // 500ms fade + 1000ms buffer
      }
    });

    // Clean up ref for optimistic moves that no longer exist
    const currentOptimisticIds = new Set(optimisticMoves.map(m => m.id));
    pendingRemovalsRef.current.forEach((id) => {
      if (!currentOptimisticIds.has(id)) {
        pendingRemovalsRef.current.delete(id);
      }
    });
  }, [timelineData, optimisticMoves, removeOptimisticMove]);

  // Age-out fallback so no optimistic tile can ever stick permanently. A still-pending
  // move is marked failed; a tile that is older than the hard limit is removed outright
  // regardless of status — this is the safety net for a 'completed' tile whose real move
  // never matched the global id check for any reason.
  useEffect(() => {
    const TIMEOUT_MS = 30000; // 30 seconds — mark a still-pending move as failed
    const HARD_REMOVE_MS = 60000; // 60 seconds — remove any lingering tile, any status

    const interval = setInterval(() => {
      const now = new Date();
      optimisticMoves.forEach((optMove) => {
        const age = now.getTime() - optMove.createdAt.getTime();
        if (age > HARD_REMOVE_MS) {
          removeOptimisticMove(optMove.id);
        } else if (age > TIMEOUT_MS && optMove.status !== 'completed' && optMove.status !== 'failed') {
          // Mark as failed if still pending after timeout
          updateOptimisticMove(optMove.id, { status: 'failed' });
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [optimisticMoves, updateOptimisticMove, removeOptimisticMove]);

  // Fast polling when waiting for optimistic moves to be ASSIGNED (not just offered)
  // This supplements the 5-second poll interval with faster checks when needed
  useEffect(() => {
    // Check if any optimistic moves have actualMoveId but haven't been assigned yet
    const assignedMoveIds = new Set<number>();
    timelineData.forEach((row) => {
      row.scenarios.forEach((scenario) => {
        scenario.moves?.forEach((moveLink) => {
          if (moveLink.move?.id) assignedMoveIds.add(moveLink.move.id);
        });
      });
      // NOTE: Intentionally NOT including offeredScenarios
      // Keep polling until move is assigned, not just offered
    });

    const pendingMoves = optimisticMoves.filter(
      (opt) => opt.actualMoveId && !assignedMoveIds.has(opt.actualMoveId)
    );

    if (pendingMoves.length === 0) return;

    // Poll every 500ms until the moves are assigned
    const interval = setInterval(() => {
      refetch();
    }, 500);

    // Check on each poll if moves are now assigned, and stop early if they are
    const checkInterval = setInterval(() => {
      // Re-check assigned moves from current timeline data
      const currentAssignedMoveIds = new Set<number>();
      timelineData.forEach((row) => {
        row.scenarios.forEach((scenario) => {
          scenario.moves?.forEach((moveLink) => {
            if (moveLink.move?.id) currentAssignedMoveIds.add(moveLink.move.id);
          });
        });
      });

      const stillPending = optimisticMoves.filter(
        (opt) => opt.actualMoveId && !currentAssignedMoveIds.has(opt.actualMoveId)
      );

      if (stillPending.length === 0) {
        clearInterval(interval);
        clearInterval(checkInterval);
      }
    }, 500);

    // Stop fast polling after 30 seconds max (increased from 10s to allow more time for assignment)
    const timeout = setTimeout(() => {
      console.log('⏱️ [FastPoll] Stopping fast polling after 30s timeout');
      clearInterval(interval);
      clearInterval(checkInterval);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [optimisticMoves, timelineData, refetch]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Typography color="error">Error loading schedule: {error.message}</Typography>
      </Box>
    );
  }

  // Show the banner area whenever at least one banner has something to say.
  const hasActiveBanner =
    businessHours.isClosed ||
    isPastClosingTimeToday ||
    (laneCalculating && !!planningAddress) ||
    noSlotsAvailable ||
    !!laneBuilderError;

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Header with date picker and search */}
      <ScheduleHeader
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onAddressSelect={businessHours.isClosed || isPastClosingTimeToday || !canCreateFromTimeline ? () => {} : handleAddressSelect}
        locations={locations}
        rooftopId={rooftop?.id}
        selectedLocation={selectedAddressForInput}
        isLocationClosed={businessHours.isClosed || isPastClosingTimeToday || !canCreateFromTimeline}
        algorithm={algorithm}
        isPlanning={!!planningAddress}
        fullPlanningEnabled={fullPlanningEnabled}
        isLoadingOverlays={isLoadingOverlays}
        noSlotsAvailable={noSlotsAvailable}
        driverCount={driverCount}
      />

      {/* Banner area - collapses in whenever any banner has something to show */}
      <Collapse in={hasActiveBanner}>
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          {/* Closed Day Banner */}
          {businessHours.isClosed && (
            <Alert severity="error" sx={{ m: 0, mb: 0 }}>
              Location closed for {businessHours.closureName || 'this day'}. Planning features are disabled.
            </Alert>
          )}

          {/* Past Closing Time Banner */}
          {isPastClosingTimeToday && !businessHours.isClosed && (
            <Alert severity="error" sx={{ m: 0, mb: 0 }}>
              Business hours have ended for today. Planning features are disabled.
            </Alert>
          )}

          {/* Lane Calculating Notice - routing estimates must land before slots can be suggested */}
          {laneCalculating && !!planningAddress && !businessHours.isClosed && !isPastClosingTimeToday && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', p: 1.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Calculating lane estimates, please wait...
              </Typography>
              <CircularProgress size={18} />
            </Box>
          )}

          {/* No Available Slots Banner */}
          {noSlotsAvailable && (
            <Alert severity="warning" sx={{ m: 0, mb: 0 }}>
              No available time slots for this address today. Please try a different date.
            </Alert>
          )}

          {/* Lane Builder Error Banner */}
          {laneBuilderError && (
            <Alert severity="error" sx={{ m: 0, mb: 0 }}>
              {laneBuilderError}
            </Alert>
          )}
        </Box>
      </Collapse>

      {/* Timeline */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Timeline
          timezoneOverride={getRooftopTimezone(rooftop)}
          componentAreaRow={ScheduleAreaRow}
          componentAreaRowProps={{
            date: selectedDate,
            onScenarioClick: handleScenarioClick,
            planningLane: (businessHours.isClosed || isPastClosingTimeToday || !canCreateFromTimeline) ? null : timelineLane,
            onTimeSlotClick: handleTimeSlotClick,
            moveBufferMinutes: moveBufferMinutes,
            dayStartTime: businessHours.openTime,
            dayEndTime: businessHours.closeTime,
            algorithm: algorithm,
            driverCount: driverCount,
            tier: isLiteTier ? 'lite' : 'standard',
            // Use filteredData to ensure indices match the rendered rows
            // filteredData already excludes unassigned row and applies location filtering
            // This ensures overlay's driverIndex correctly matches allDriverScenarios array index
            allDriverScenarios: filteredData
              .map((row) => row.allScenarios),
            offsetIncrementMinutes: offsetIncrementMinutes,
            offsetMaxMinutes: offsetMaxMinutes,
            scenarioKey: planningScenarioKey,
            globalRealMoveIds: globalRealMoveIds,
          }}
          componentIndexRow={ScheduleIndexRow}
          componentIndexRowProps={{
            isSelected: (dataItem: any) => dataItem.driver?.id === selectedDriverId,
            onClick: (dataItem: any) => handleDriverClick(dataItem.driver?.id || null),
            isLite: isLiteTier,
            selectedDate: selectedDate,
            timezone: rooftop?.location?.timezone || dayjs.tz.guess(),
          }}
          data={filteredData}
          date={selectedDate}
          markers={[
            { time: timelineStart, label: 'Start Time', color: theme.palette.grey[500] },
            { time: timelineEnd, label: 'End Time', color: theme.palette.grey[500] },
          ]}
          scrollKey="schedule-timeline"
          sidebarSize={220}
          rowSize={80}
          zoom={timelineZoom}
          showMarkers={true}
          onTimelineReady={(scrollFn) => setTimelineScrollFn(() => scrollFn)}
        />
      </Box>

      {/* Side Panel - View or Create Mode */}
      {/* Use isPanelVisible for visual open/close, but keep panelMode for mounting */}
      {/* keepMounted=true when panelMode='create' ensures MovePlannerProvider stays alive during dispatch */}
      {/* even when panel is visually closed - panelMode is only set to null after dispatch completes */}
      <SidePanel
        open={isPanelVisible}
        onClose={handlePanelClose}
        title={panelMode === 'view' ? 'Move Details' : panelMode === 'create' ? 'Create Move' : ''}
        keepMounted={panelMode === 'create'}
      >
        {panelMode === 'view' && (
          <MoveDetailsPanelContent move={selectedMove} onClose={handlePanelClose} />
        )}
        {panelMode === 'create' && (
          <MoveCreationPanelContent
            planningContext={planningContext}
            onClose={handlePanelClose}
            onSuccess={handleMoveCreated}
            onDispatchComplete={handleDispatchComplete}
            onSubmitStart={handleSubmitStart}
            isVisible={isPanelVisible}
          />
        )}
      </SidePanel>
    </Box>
  );
};

// Wrapper component that provides the OptimisticMoveContext
const ScheduleTimelineWithProvider = (props: ScheduleTimelineProps) => {
  return (
    <OptimisticMoveProvider>
      <ScheduleTimeline {...props} />
    </OptimisticMoveProvider>
  );
};

export default ScheduleTimelineWithProvider;
